import React, { useEffect, useState } from "react";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { db } from "../config/firebase";
import PageTemplate from "../templates/PageTemplate";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

const SECTIONS = [
  { key: "terms", label: "Conditions d'utilisation" },
  { key: "privacy", label: "Politique de confidentialité" },
  { key: "mentions", label: "Mentions légales" },
];

const QUILL_MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ["bold", "italic", "underline", "strike"],
    [{ color: [] }, { background: [] }],
    [{ list: "ordered" }, { list: "bullet" }],
    [{ align: [] }],
    ["blockquote"],
    ["link"],
    ["clean"],
  ],
};

const QUILL_FORMATS = [
  "header",
  "bold",
  "italic",
  "underline",
  "strike",
  "color",
  "background",
  "list",
  "bullet",
  "align",
  "blockquote",
  "link",
];

const DEFAULT_TERMS = `<p>Bienvenue sur Zzigs. En utilisant notre application mobile, vous acceptez les présentes conditions d'utilisation. Zzigs est une plateforme qui met en relation des couturiers professionnels avec des clients recherchant des services de couture personnalisés, et facilite les transactions sécurisées entre eux.</p>
<h2>1. Acceptation des conditions</h2>
<p>En accédant et en utilisant l'application Zzigs, vous acceptez d'être lié par ces conditions d'utilisation. Si vous n'acceptez pas ces conditions, veuillez ne pas utiliser notre service.</p>
<h2>2. Description du service</h2>
<p>Zzigs propose une plateforme permettant de :</p>
<ul><li>Mettre en relation des clients avec des couturiers qualifiés</li><li>Faciliter la communication et la négociation des services</li><li>Gérer les demandes et projets de couture</li><li>Traiter les paiements de manière sécurisée via l'application</li><li>Assurer un suivi transparent des prestations</li></ul>
<p><strong>Note :</strong> L'inscription et l'utilisation de Zzigs sont actuellement gratuites pour les couturiers. Le modèle tarifaire peut évoluer à l'avenir, les utilisateurs en seront informés préalablement.</p>
<h2>3. Inscription et compte utilisateur</h2>
<p>Pour utiliser Zzigs, vous devez créer un compte en fournissant des informations exactes et à jour. Vous êtes responsable de la confidentialité de vos identifiants et de toutes les activités effectuées depuis votre compte.</p>
<blockquote>Vous vous engagez à notifier immédiatement Zzigs de toute utilisation non autorisée de votre compte.</blockquote>
<h2>4. Obligations des utilisateurs</h2>
<p>En utilisant Zzigs, vous vous engagez à :</p>
<ul><li>Fournir des informations véridiques et exactes</li><li>Respecter les autres utilisateurs et maintenir une communication professionnelle</li><li>Ne pas utiliser le service à des fins illégales ou frauduleuses</li><li>Respecter les lois en vigueur dans votre juridiction</li></ul>
<h2>5. Relations entre utilisateurs</h2>
<p>Zzigs agit en tant qu'intermédiaire entre les couturiers et les clients en facilitant leur mise en relation et en gérant les transactions financières via la plateforme. Les couturiers sont responsables de la qualité de leur travail et du respect des délais convenus avec les clients.</p>
<blockquote><strong>Important :</strong> Tout litige concernant la qualité du service doit être résolu directement entre les parties concernées. Zzigs peut fournir une assistance mais n'est pas responsable de la qualité des prestations effectuées par les couturiers.</blockquote>
<h2>6. Paiements et frais</h2>
<p>Zzigs facilite les transactions financières entre clients et couturiers via un système de paiement intégré et sécurisé.</p>
<h3>Pour les clients :</h3>
<p>Les paiements des prestations s'effectuent directement via l'application Zzigs. Les prix sont convenus entre le client et le couturier avant le début du projet. Zzigs peut prélever des frais de service ou une commission sur chaque transaction, qui seront clairement indiqués avant la validation du paiement.</p>
<h3>Pour les couturiers :</h3>
<p>L'inscription et l'utilisation de la plateforme sont actuellement gratuites. Les paiements des clients sont transférés aux couturiers selon les modalités définies dans l'application, déduction faite des éventuels frais de service de la plateforme.</p>
<blockquote><strong>Sécurité :</strong> Tous les paiements sont traités de manière sécurisée via des partenaires de paiement certifiés. Vos informations bancaires sont protégées et jamais stockées sur nos serveurs.</blockquote>
<p>Zzigs se réserve le droit de modifier sa structure tarifaire à tout moment. Les utilisateurs seront informés de tout changement avec un préavis raisonnable.</p>
<h2>7. Propriété intellectuelle</h2>
<p>Tous les contenus présents sur Zzigs, y compris le logo, les textes, graphiques, et le code source, sont la propriété de Zzigs et sont protégés par les lois sur la propriété intellectuelle. Toute reproduction non autorisée est strictement interdite.</p>
<h2>8. Limitation de responsabilité</h2>
<p>Zzigs ne peut être tenu responsable des dommages directs ou indirects résultant de l'utilisation ou de l'impossibilité d'utiliser le service. Nous ne garantissons pas que le service sera ininterrompu ou exempt d'erreurs.</p>
<p>Zzigs n'est pas responsable de la qualité des services fournis par les couturiers, des retards de livraison, ou de tout différend entre utilisateurs. Notre responsabilité se limite à la fourniture de la plateforme technologique de mise en relation.</p>
<h2>9. Modification des conditions</h2>
<p>Zzigs se réserve le droit de modifier ces conditions d'utilisation à tout moment. Les utilisateurs seront informés des changements importants par notification dans l'application ou par email. L'utilisation continue du service après modification vaut acceptation des nouvelles conditions.</p>
<h2>10. Résiliation</h2>
<p>Vous pouvez fermer votre compte à tout moment depuis les paramètres de l'application. Zzigs se réserve le droit de suspendre ou de résilier votre compte en cas de violation de ces conditions d'utilisation, de fraude, ou de comportement inapproprié.</p>
<h2>Contact</h2>
<p>Pour toute question concernant ces conditions d'utilisation, veuillez nous contacter :</p>
<p>Email de support : <a href="mailto:contact@zzigs.com">contact@zzigs.com</a></p>`;

const DEFAULT_PRIVACY = `<p>Chez Zzigs, nous accordons une grande importance à la protection de vos données personnelles. Cette politique de confidentialité explique quelles informations nous collectons, comment nous les utilisons, et quels sont vos droits concernant vos données.</p>
<blockquote><strong>En bref :</strong> Nous collectons uniquement les données nécessaires au fonctionnement de notre service, nous ne vendons jamais vos informations personnelles, et vous avez le contrôle total sur vos données.</blockquote>
<h2>1. Données collectées</h2>
<p>Nous collectons différents types d'informations pour vous fournir et améliorer notre service :</p>
<h3>Informations d'identification</h3>
<p>Nom, prénom, adresse email, numéro de téléphone, photo de profil</p>
<h3>Informations de localisation</h3>
<p>Adresse, code postal, ville (pour faciliter la mise en relation avec les couturiers locaux)</p>
<h3>Informations de paiement</h3>
<p>Informations bancaires traitées de manière sécurisée par nos prestataires de paiement certifiés (nous ne stockons pas vos coordonnées bancaires complètes)</p>
<h3>Données d'utilisation</h3>
<p>Historique des projets, messages échangés, évaluations, préférences d'utilisation</p>
<h3>Données techniques</h3>
<p>Type d'appareil, système d'exploitation, adresse IP, données de navigation</p>
<h2>2. Utilisation des données</h2>
<p>Nous utilisons vos données personnelles pour les finalités suivantes :</p>
<ul><li>Créer et gérer votre compte utilisateur</li><li>Faciliter la mise en relation entre clients et couturiers</li><li>Traiter les paiements et transactions de manière sécurisée</li><li>Vous envoyer des notifications importantes concernant vos projets</li><li>Améliorer et personnaliser votre expérience utilisateur</li><li>Prévenir la fraude et assurer la sécurité de la plateforme</li><li>Vous envoyer des communications marketing (avec votre consentement)</li><li>Respecter nos obligations légales et réglementaires</li></ul>
<h2>3. Partage des données</h2>
<p>Nous ne vendons jamais vos données personnelles. Nous pouvons partager vos informations uniquement dans les cas suivants :</p>
<h3>Avec les autres utilisateurs</h3>
<p>Votre profil (nom, photo, évaluations) est visible par les autres utilisateurs pour faciliter la mise en relation</p>
<h3>Avec nos prestataires de services</h3>
<p>Fournisseurs de paiement, hébergeurs, services d'analyse, etc. (tous sous contrat de confidentialité strict)</p>
<h3>Pour des raisons légales</h3>
<p>Si requis par la loi, une décision de justice, ou pour protéger nos droits et notre sécurité</p>
<h2>4. Conservation des données</h2>
<p>Nous conservons vos données personnelles aussi longtemps que nécessaire pour vous fournir nos services et respecter nos obligations légales :</p>
<ul><li>Données de compte : Pendant toute la durée d'activité de votre compte</li><li>Données de transaction : Conservées pendant 10 ans (obligation légale)</li><li>Données de navigation : Maximum 13 mois</li><li>Après suppression : 30 jours de conservation de sauvegarde puis suppression définitive</li></ul>
<h2>5. Sécurité des données</h2>
<p>Nous mettons en œuvre des mesures de sécurité techniques et organisationnelles appropriées pour protéger vos données :</p>
<h3>Chiffrement SSL/TLS</h3>
<p>Toutes les données en transit sont chiffrées</p>
<h3>Serveurs sécurisés</h3>
<p>Hébergement sur des serveurs certifiés</p>
<h3>Accès restreint</h3>
<p>Seules les personnes autorisées y ont accès</p>
<h3>Sauvegardes régulières</h3>
<p>Protection contre la perte de données</p>
<h2>6. Vos droits</h2>
<p>Conformément au RGPD et aux lois applicables, vous disposez des droits suivants :</p>
<ul><li><strong>Droit d'accès :</strong> Obtenir une copie de vos données personnelles</li><li><strong>Droit de rectification :</strong> Corriger vos données inexactes ou incomplètes</li><li><strong>Droit à l'effacement :</strong> Supprimer vos données dans certaines conditions</li><li><strong>Droit à la portabilité :</strong> Récupérer vos données dans un format structuré</li><li><strong>Droit d'opposition :</strong> Refuser certains traitements de vos données</li><li><strong>Droit de limitation :</strong> Limiter le traitement de vos données</li></ul>
<p>Pour exercer vos droits, contactez-nous à <a href="mailto:contact@zzigs.com">contact@zzigs.com</a>. Nous répondrons dans un délai de 30 jours maximum.</p>
<h2>7. Cookies et technologies similaires</h2>
<p>Nous utilisons des cookies et technologies similaires pour améliorer votre expérience. Vous pouvez gérer vos préférences de cookies dans les paramètres de l'application ou de votre navigateur.</p>
<ul><li><strong>Cookies essentiels :</strong> Nécessaires au fonctionnement de l'app</li><li><strong>Cookies analytiques :</strong> Pour comprendre l'utilisation de l'app</li><li><strong>Cookies de préférence :</strong> Pour mémoriser vos choix</li></ul>
<h2>8. Données des mineurs</h2>
<p>Zzigs est réservé aux personnes âgées de 18 ans et plus. Nous ne collectons pas sciemment de données personnelles auprès de mineurs. Si vous pensez qu'un mineur nous a fourni des données, contactez-nous immédiatement.</p>
<h2>9. Transferts internationaux</h2>
<p>Vos données peuvent être transférées et traitées dans des pays situés en dehors de l'Espace économique européen (EEE). Dans ce cas, nous nous assurons que des garanties appropriées sont mises en place pour protéger vos données conformément au RGPD.</p>
<p>Nous travaillons uniquement avec des prestataires qui offrent des garanties de protection des données équivalentes aux normes européennes (clauses contractuelles types, Privacy Shield, etc.).</p>
<h2>10. Modifications de la politique</h2>
<p>Nous pouvons modifier cette politique de confidentialité à tout moment. En cas de changements importants, nous vous informerons par notification dans l'application ou par email. La date de dernière mise à jour sera toujours indiquée en haut de cette page.</p>
<h2>11. Réclamations</h2>
<p>Si vous estimez que vos droits en matière de protection des données ne sont pas respectés, vous avez le droit de déposer une réclamation auprès de l'autorité de contrôle compétente :</p>
<p>CNIL (Commission Nationale de l'Informatique et des Libertés)<br>3 Place de Fontenoy<br>TSA 80715<br>75334 Paris Cedex 07<br>France<br><a href="https://www.cnil.fr">www.cnil.fr</a></p>
<h2>Contact - Délégué à la protection des données</h2>
<p>Pour toute question concernant cette politique de confidentialité ou l'exercice de vos droits, vous pouvez nous contacter :</p>
<p>Email de support : <a href="mailto:contact@zzigs.com">contact@zzigs.com</a></p>
<p>Nous nous engageons à répondre à toutes vos demandes dans un délai de 30 jours maximum.</p>`;

const DEFAULT_MENTIONS = `<h2>1. Éditeur du site et de l'application</h2>
<p>Le site <a href="https://zzigs.com">zzigs.com</a> et l'application mobile Zzigs sont édités par :</p>
<ul><li><strong>Nom :</strong> Zzigs</li><li><strong>Email :</strong> <a href="mailto:contact@zzigs.com">contact@zzigs.com</a></li><li><strong>Site web :</strong> <a href="https://zzigs.com">https://zzigs.com</a></li></ul>
<h2>2. Directeur de la publication</h2>
<p>Le directeur de la publication est le représentant légal de Zzigs.</p>
<p>Contact : <a href="mailto:contact@zzigs.com">contact@zzigs.com</a></p>
<h2>3. Hébergement</h2>
<p>Le site et les données sont hébergés par :</p>
<ul><li><strong>Firebase (Google Cloud Platform)</strong> — Google LLC, 1600 Amphitheatre Parkway, Mountain View, CA 94043, États-Unis</li><li><strong>OVH</strong> — 2 rue Kellermann, 59100 Roubaix, France (services email)</li></ul>
<h2>4. Description du service</h2>
<p>Zzigs est une plateforme numérique de mise en relation entre des couturiers et couturières professionnels et des clients recherchant des services de couture personnalisés : retouches, créations sur mesure, services à domicile ou en atelier.</p>
<p>L'application est disponible sur :</p>
<ul><li><strong>App Store (iOS) :</strong> <a href="https://itunes.apple.com/app/id6753733348">Télécharger sur l'App Store</a></li><li><strong>Google Play (Android) :</strong> <a href="https://play.google.com/store/apps/details?id=com.zzigs">Télécharger sur Google Play</a></li></ul>
<h2>5. Propriété intellectuelle</h2>
<p>L'ensemble du contenu du site et de l'application Zzigs (textes, images, logo, graphismes, icônes, code source) est la propriété exclusive de Zzigs ou de ses partenaires, et est protégé par les lois françaises et internationales relatives à la propriété intellectuelle.</p>
<p>Toute reproduction, représentation, modification, distribution ou exploitation de tout ou partie de ces éléments sans l'autorisation écrite préalable de Zzigs est strictement interdite.</p>
<h2>6. Données personnelles</h2>
<p>Zzigs s'engage à protéger les données personnelles de ses utilisateurs conformément au Règlement Général sur la Protection des Données (RGPD) et à la loi Informatique et Libertés.</p>
<p>Pour en savoir plus sur la collecte, le traitement et la protection de vos données personnelles, veuillez consulter notre <a href="/privacy.php">Politique de confidentialité</a>.</p>
<p><strong>Responsable du traitement :</strong> Zzigs — <a href="mailto:contact@zzigs.com">contact@zzigs.com</a></p>
<h2>7. Cookies</h2>
<p>Le site zzigs.com utilise des cookies pour améliorer l'expérience utilisateur, analyser le trafic et assurer le bon fonctionnement des services. Vous pouvez gérer vos préférences de cookies dans les paramètres de votre navigateur.</p>
<p>Types de cookies utilisés :</p>
<ul><li><strong>Cookies essentiels :</strong> nécessaires au fonctionnement du site</li><li><strong>Cookies analytiques :</strong> Google Analytics (anonymisés) pour comprendre l'utilisation du site</li><li><strong>Cookies de préférence :</strong> pour mémoriser vos choix</li></ul>
<h2>8. Paiements</h2>
<p>Les transactions financières sur Zzigs sont traitées de manière sécurisée par <strong>Stripe</strong> (Stripe, Inc., 510 Townsend Street, San Francisco, CA 94103, États-Unis). Zzigs ne stocke jamais les coordonnées bancaires complètes de ses utilisateurs.</p>
<h2>9. Limitation de responsabilité</h2>
<p>Zzigs agit en tant qu'intermédiaire technique de mise en relation. À ce titre :</p>
<ul><li>Zzigs ne peut être tenu responsable de la qualité des prestations réalisées par les couturiers</li><li>Zzigs ne garantit pas la disponibilité ininterrompue du service</li><li>Zzigs ne peut être tenu responsable des litiges entre utilisateurs</li></ul>
<h2>10. Droit applicable et juridiction</h2>
<p>Les présentes mentions légales sont soumises au droit français. En cas de litige, et après tentative de résolution amiable, les tribunaux français seront seuls compétents.</p>
<h2>11. Contact</h2>
<p>Pour toute question relative aux présentes mentions légales, vous pouvez nous contacter :</p>
<p>Email : <a href="mailto:contact@zzigs.com">contact@zzigs.com</a></p>`;

export default function Documents() {
  const [data, setData] = useState({});
  const [activeTab, setActiveTab] = useState("terms");
  const [isLoading, setIsLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [savedData, setSavedData] = useState({});

  const DEFAULTS = { terms: DEFAULT_TERMS, privacy: DEFAULT_PRIVACY, mentions: DEFAULT_MENTIONS };

  useEffect(() => {
    async function fetchAll() {
      setIsLoading(true);
      const docRef = doc(db, "CGV", "CGV");
      const docSnap = await getDoc(docRef);
      const d = docSnap.exists() ? docSnap.data() : {};
      const merged = { ...d };
      let needsSave = false;
      for (const [key, defaultVal] of Object.entries(DEFAULTS)) {
        if (!d[key] || d[key] === "<p><br></p>") {
          merged[key] = defaultVal;
          needsSave = true;
        }
      }
      if (needsSave) {
        await setDoc(docRef, merged, { merge: true });
      }
      setData(merged);
      setSavedData(merged);
      setIsLoading(false);
    }
    fetchAll();
  }, []);

  const handleChange = (value) => {
    setData((prev) => ({ ...prev, [activeTab]: value }));
    setHasChanges(true);
  };

  const showNotif = (msg) => {
    const div = document.createElement("div");
    div.className =
      "fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 text-sm";
    div.textContent = msg;
    document.body.appendChild(div);
    setTimeout(() => div.remove(), 3000);
  };

  const update = async () => {
    setSaving(true);
    const docRef = doc(db, "CGV", "CGV");
    try {
      await setDoc(docRef, { [activeTab]: data[activeTab] }, { merge: true });
      setSavedData((prev) => ({ ...prev, [activeTab]: data[activeTab] }));
      showNotif("Document enregistré !");
      setHasChanges(false);
    } catch (error) {
      console.error("Erreur:", error);
      alert("Erreur lors de la sauvegarde");
    } finally {
      setSaving(false);
    }
  };

  const handleTabSwitch = (key) => {
    setActiveTab(key);
    setHasChanges(data[key] !== savedData[key]);
  };

  return (
    <PageTemplate
      loading={isLoading}
      content={
        <div className="space-y-4">
          {/* Tabs */}
          <div className="flex flex-wrap items-center gap-1 bg-white rounded-lg border border-gray-200 p-1">
            {SECTIONS.map(({ key, label }) => (
              <button
                key={key}
                onClick={() => handleTabSwitch(key)}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                  activeTab === key
                    ? "bg-gray-900 text-white"
                    : "text-gray-600 hover:bg-gray-100"
                }`}
              >
                {label}
              </button>
            ))}
            {hasChanges && (
              <span className="ml-auto text-xs text-orange-600 font-medium px-2">
                Non sauvegardé
              </span>
            )}
          </div>

          {/* Editor */}
          <div className="bg-white rounded-lg border border-gray-200">
            <div className="flex items-center justify-between px-3 py-2 border-b border-gray-200">
              <span className="text-xs font-medium text-gray-900">
                {SECTIONS.find((s) => s.key === activeTab)?.label}
              </span>
              <div className="flex items-center gap-2">
                <button
                  onClick={update}
                  disabled={saving || !hasChanges}
                  className="px-3 py-1 bg-[#801111] text-white rounded text-xs font-medium hover:bg-[#5a0c0c] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving ? "Sauvegarde..." : "Enregistrer"}
                </button>
              </div>
            </div>
            <div className="quill-editor-wrapper">
              <ReactQuill
                key={activeTab}
                theme="snow"
                value={data[activeTab] || ""}
                onChange={handleChange}
                modules={QUILL_MODULES}
                formats={QUILL_FORMATS}
                placeholder="Saisissez le contenu du document ici..."
              />
            </div>
          </div>

          {/* Info */}
          <p className="text-[10px] text-gray-400">
            Le contenu est sauvegardé en HTML et affiché directement sur le site web.
          </p>
        </div>
      }
    />
  );
}
